import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { dangNhapGoogle, theoDoiDangNhap } from '../lib/auth.js';
import { layDb } from '../lib/firebase.js';
import { FUNDS_GOC } from '../data/funds.js';
import { docTatCaQuy, luuQuy, xoaQuy, nhapQuyTuCode } from '../lib/fundsAdmin.js';
import {
  chuanHoaQuy, chuanHoaSlug, kiemTraQuy, canXemLaiQuy, tienTuForm, soTuTien,
  CO_SO_TICKET, LOAI_NGUON_TICKET, DO_TIN_CAY,
} from '../lib/fundModel.js';
import { fundCta, fundTypeLabel, ticketText } from '../lib/fundLabels.js';
import { options } from '@shared/taxonomy.js';
import { homNay as layHomNay } from '../lib/format.js';

/**
 * /admin/nguon-von — thêm, sửa, ẩn quỹ và nguồn vốn mà không cần sửa code.
 * Quyền giống /admin/chuong-trinh: admins/{uid}.role = 'editor' | 'admin'.
 */
const TY_GIA_MAC_DINH = 26170;
const EMPTY = chuanHoaQuy({ type: 'VC', vietnamPresence: 'OFFICE', thesis: { capitalTypes: ['EQUITY'], geos: ['VN'] } });
const NHAN_TT = { DRAFT: 'Bản nháp', PUBLISHED: 'Đang hiện', HIDDEN: 'Đã ẩn' };

function useQuyenQuanTri() {
  const [s, setS] = useState({ user: null, role: '', checking: true });
  useEffect(() => theoDoiDangNhap(async (next) => {
    setS({ user: next, role: '', checking: true });
    let role = '';
    if (next) try {
      const db = await layDb();
      const { doc, getDoc } = await import('firebase/firestore/lite');
      const snap = await getDoc(doc(db, 'admins', next.uid));
      role = snap.exists() ? snap.data()?.role || '' : '';
    } catch { role = ''; }
    setS({ user: next, role, checking: false });
  }), []);
  return { ...s, allowed: ['admin', 'editor'].includes(s.role) };
}

const dong = (arr) => (arr || []).join('\n');
const tachDong = (s) => String(s || '').split('\n').map((x) => x.trim()).filter(Boolean);
const nguonThanhChu = (sources) => (sources || []).map((s) => [s.url, s.note].filter(Boolean).join(' | ')).join('\n');
const chuThanhNguon = (text, ngay, cu = []) => tachDong(text).map((line) => {
  const [url, ...ghiChu] = line.split('|');
  const u = url.trim();
  return { url: u, note: ghiChu.join('|').trim(), fetchedAt: cu.find((s) => s.url === u)?.fetchedAt || ngay };
});
function formTicket(t, homNay) {
  const m = t?.min || t?.max;
  return {
    minSo: soTuTien(t?.min), maxSo: soTuTien(t?.max),
    ccy: m?.ccy || 'USD', fx: String(m?.fx || TY_GIA_MAC_DINH), fxDate: m?.fxDate || homNay,
  };
}

function HopChon({ nhan, danhSach, chon, onDoi, goiY }) {
  return <div className="o-rong"><label>{nhan}</label>{goiY && <p className="nho">{goiY}</p>}<div className="admin-checks">
    {danhSach.map((o) => <label key={o.value}><input type="checkbox" checked={(chon || []).includes(o.value)}
      onChange={(e) => onDoi(o.value, e.target.checked)} /> {o.label}</label>)}
  </div></div>;
}

function XoaVinhVien({ id, onXong, setMessage }) {
  const [mo, setMo] = useState(false); const [go, setGo] = useState(''); const [busy, setBusy] = useState(false);
  if (!mo) return <div className="admin-danger-toggle"><button type="button" className="text-button danger" onClick={() => setMo(true)}>Xoá vĩnh viễn…</button></div>;
  async function xoa() {
    setBusy(true);
    try { await xoaQuy(id); setMessage('Đã xoá khỏi Firestore.'); onXong(); }
    catch (e) { setMessage(e?.code === 'permission-denied' ? 'Chỉ tài khoản role admin mới xoá được. Muốn gỡ khỏi website, dùng “Ẩn khỏi website”.' : e.message); }
    finally { setBusy(false); }
  }
  return <div className="admin-danger">
    <p><strong>Xoá vĩnh viễn “{id}”?</strong></p>
    <p className="nho">Thường chỉ cần “Ẩn khỏi website”. Nếu quỹ có sẵn trong code, xoá ở đây sẽ làm bản trong code hiện lại.</p>
    <label htmlFor="xoa-quy">Gõ lại <code>{id}</code> để xác nhận</label>
    <input id="xoa-quy" type="text" value={go} autoComplete="off" onChange={(e) => setGo(e.target.value)} />
    <div className="admin-danger-actions">
      <button type="button" className="nut nut-nguy-hiem" disabled={go.trim() !== id || busy} onClick={xoa}>{busy ? 'Đang xoá…' : 'Xoá vĩnh viễn'}</button>
      <button type="button" className="text-button" onClick={() => { setMo(false); setGo(''); }}>Huỷ</button>
    </div>
  </div>;
}

export default function FundAdmin() {
  const { user, role, allowed, checking } = useQuyenQuanTri();
  const homNay = layHomNay();
  const [list, setList] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [tk, setTk] = useState(() => formTicket(null, homNay));
  const [nguonText, setNguonText] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [chiCanXem, setChiCanXem] = useState(false);

  const daLuu = list.some((f) => f.fundId === draft.fundId && draft.updatedAt);
  const slugNow = draft.slug || chuanHoaSlug(draft.name);
  const chuaNhap = FUNDS_GOC.filter((f) => !f.isExample && !list.some((x) => x.fundId === f.fundId));

  async function refresh() {
    try { setList((await docTatCaQuy()).sort((a, b) => a.name.localeCompare(b.name, 'vi'))); }
    catch (e) { setMessage(e.message); }
  }
  useEffect(() => { if (allowed) refresh(); }, [allowed]);

  const set = (k, v) => setDraft((o) => ({ ...o, [k]: v }));
  const setIn = (k, sub, v) => setDraft((o) => ({ ...o, [k]: { ...(o[k] || {}), [sub]: v } }));
  const batIn = (k, sub, v, on) => setDraft((o) => {
    const cu = o[k]?.[sub] || [];
    return { ...o, [k]: { ...(o[k] || {}), [sub]: on ? [...new Set([...cu, v])] : cu.filter((x) => x !== v) } };
  });
  const setTicket = (sub, v) => setDraft((o) => ({ ...o, thesis: { ...o.thesis, ticket: { ...(o.thesis.ticket || {}), [sub]: v } } }));

  function mo(f) {
    setDraft(f); setTk(formTicket(f.thesis?.ticket, homNay));
    setNguonText(nguonThanhChu(f.provenance?.sources)); setMessage(''); window.scrollTo(0, 0);
  }
  function moi() { setDraft(EMPTY); setTk(formTicket(null, homNay)); setNguonText(''); setMessage(''); }

  const banGhi = useMemo(() => {
    const min = tienTuForm({ so: tk.minSo, ccy: tk.ccy, fx: tk.fx, fxDate: tk.fxDate });
    const max = tienTuForm({ so: tk.maxSo, ccy: tk.ccy, fx: tk.fx, fxDate: tk.fxDate });
    return chuanHoaQuy({
      ...draft,
      slug: slugNow,
      fundId: daLuu ? draft.fundId : (draft.fundId && draft.fundId !== EMPTY.fundId ? draft.fundId : slugNow),
      thesis: { ...draft.thesis, ticket: { ...(draft.thesis?.ticket || {}), min, max } },
      provenance: { ...draft.provenance, sources: chuThanhNguon(nguonText, draft.provenance?.lastVerifiedAt || homNay, draft.provenance?.sources) },
    });
  }, [draft, tk, slugNow, nguonText, daLuu, homNay]);
  const loi = kiemTraQuy(banGhi);
  const cta = fundCta(banGhi);

  async function luu(status) {
    const moiTao = !list.some((f) => f.fundId === banGhi.fundId);
    if (moiTao && FUNDS_GOC.some((f) => f.fundId === banGhi.fundId || f.slug === banGhi.slug) && !FUNDS_GOC.some((f) => f.fundId === draft.fundId)) {
      setMessage(`"${banGhi.slug}" là quỹ có sẵn trong code. Bấm “Chép quỹ có sẵn vào đây” rồi sửa bản đó.`); return;
    }
    if (status === 'PUBLISHED' && loi.length) { setMessage('Chưa xuất bản được: ' + loi.join(' ')); return; }
    setBusy(true); setMessage('Đang lưu…');
    try {
      const saved = await luuQuy(banGhi, status, user?.email || '');
      const n = chuanHoaQuy(saved); setDraft(n); setTk(formTicket(n.thesis?.ticket, homNay));
      setMessage({
        PUBLISHED: 'Đã xuất bản. Danh mục /nguon-von và công cụ đối chiếu dùng ngay; trang HTML cho Google cập nhật ở lần deploy kế tiếp.',
        DRAFT: 'Đã lưu bản nháp (chưa hiện trên website).',
        HIDDEN: 'Đã ẩn khỏi website. Bấm “Xuất bản” để hiện lại.',
      }[status]);
      refresh();
    } catch (e) {
      setMessage(e?.code === 'permission-denied' ? 'Firestore từ chối lưu. Kiểm tra quyền của tài khoản và Firestore Rules đã deploy chưa.' : e.message);
    } finally { setBusy(false); }
  }

  async function nhap() {
    setBusy(true); setMessage('Đang chép quỹ có sẵn…');
    try { const n = await nhapQuyTuCode(list.map((f) => f.fundId), user?.email || ''); setMessage(`Đã chép ${n} quỹ. Từ giờ sửa chúng ngay trên trang này.`); refresh(); }
    catch (e) { setMessage(e?.code === 'permission-denied' ? 'Firestore từ chối lưu (kiểm tra Rules đã deploy chưa).' : e.message); }
    finally { setBusy(false); }
  }

  if (checking) return <div className="surface section-space"><p>Đang kiểm tra quyền quản trị…</p></div>;
  if (!user) return <section className="surface admin-gate"><h1>Quản trị nguồn vốn</h1><p>Đăng nhập bằng tài khoản đã được cấp quyền.</p><button className="nut" onClick={() => dangNhapGoogle()}>Đăng nhập với Google</button></section>;
  if (!allowed) return <section className="surface admin-gate"><h1>Chưa có quyền quản trị</h1><p>Tài khoản <strong>{user.email}</strong> chưa nằm trong danh sách admin.</p><p className="nho">Gửi mã này cho quản trị viên chính để được cấp quyền: <code>{user.uid}</code></p></section>;

  const th = draft.thesis || {};
  const t = th.ticket || {};
  const nhacCua = (f) => (f.publish.status === 'PUBLISHED' ? canXemLaiQuy(f, homNay) : []);
  const soCanXem = list.filter((f) => nhacCua(f).length).length;
  const hienThi = list.filter((f) => !chiCanXem || nhacCua(f).length);
  const triState = (v) => (v === true ? 'CO' : v === false ? 'KHONG' : '');
  const tuTriState = (v) => (v === 'CO' ? true : v === 'KHONG' ? false : undefined);

  return <div className="admin-playbook">
    <header className="page-heading"><p className="eyebrow">Khu vực quản trị · <Link to="/admin/playbook">Playbook</Link> · <Link to="/admin/mau">Thư viện mẫu</Link> · <Link to="/admin/chuong-trinh">Chương trình</Link> · Nguồn vốn</p>
      <h1>Danh mục nguồn vốn</h1>
      <p>Thêm quỹ, cập nhật séc đầu tư, kênh nộp hồ sơ và ngày kiểm chứng. Thay đổi dùng ngay ở <Link to="/nguon-von">/nguon-von</Link> và công cụ đối chiếu.</p></header>
    <div className="admin-layout">
      <aside className="surface admin-list">
        <div className="hang-nut"><button className="nut" onClick={moi}>+ Quỹ mới</button></div>
        {soCanXem > 0 && <p className="nho"><label><input type="checkbox" checked={chiCanXem} onChange={(ev) => setChiCanXem(ev.target.checked)} /> Chỉ hiện {soCanXem} quỹ cần xem lại</label></p>}
        {hienThi.length === 0 && <p className="nho">{list.length ? 'Không có quỹ nào cần xem lại.' : 'Chưa có quỹ nào trên trang quản trị.'}</p>}
        {hienThi.map((f) => <button className="admin-post-row" key={f.fundId} onClick={() => mo(f)}>
          <strong>{f.name || f.fundId}</strong>
          <span>{NHAN_TT[f.publish.status]} · {fundTypeLabel(f)} · kiểm chứng {f.provenance?.lastVerifiedAt || '—'}</span>
          {nhacCua(f).map((x) => <span className="badge-canh-bao" key={x}>{x}</span>)}
        </button>)}
        {chuaNhap.length > 0 && <>
          <h2>Có sẵn trong code</h2>
          <p className="nho">{chuaNhap.length} quỹ đang hiện trên website nhưng chưa sửa được ở đây.</p>
          <button className="nut phu" disabled={busy} onClick={nhap}>Chép quỹ có sẵn vào đây</button>
          <ul className="nho">{chuaNhap.map((f) => <li key={f.fundId}>{f.name}</li>)}</ul>
        </>}
      </aside>

      <section className="surface admin-editor">
        <p role="status" className="admin-message">{message}</p>
        <div className="o-nhap admin-fields">
          <div className="o-rong"><label>Tên quỹ / đơn vị</label><input type="text" value={draft.name} onChange={(ev) => set('name', ev.target.value)} /></div>
          <div><label>Tên ngắn</label><input type="text" value={draft.shortName} onChange={(ev) => set('shortName', ev.target.value)} /></div>
          <div><label>Đường dẫn {daLuu && <span className="nho">(đã khoá)</span>}</label>
            <input type="text" value={draft.slug} placeholder={chuanHoaSlug(draft.name)} disabled={daLuu} onChange={(ev) => set('slug', chuanHoaSlug(ev.target.value))} /></div>
          <div><label>Loại đơn vị</label><select value={draft.type} onChange={(ev) => set('type', ev.target.value)}>{options('FUND_TYPE').map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
          <div><label>Website chính thức</label><input type="url" value={draft.website} placeholder="https://…" onChange={(ev) => set('website', ev.target.value)} /></div>
          <div><label>Hiện diện tại Việt Nam</label><select value={draft.vietnamPresence} onChange={(ev) => set('vietnamPresence', ev.target.value)}><option value="">— chưa chọn —</option>{options('VN_PRESENCE').map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
          <div className="o-rong"><label>Căn cứ về hiện diện tại Việt Nam</label><input type="text" value={draft.vietnamEvidence} placeholder="Ví dụ: đã đầu tư vào X (2025), theo trang danh mục đầu tư của quỹ" onChange={(ev) => set('vietnamEvidence', ev.target.value)} /></div>

          <div className="o-rong"><h2>Khẩu vị đầu tư</h2></div>
          <HopChon nhan="Loại vốn" danhSach={options('CAPITAL_TYPE')} chon={th.capitalTypes} onDoi={(v, on) => batIn('thesis', 'capitalTypes', v, on)}
            goiY="Quỹ chỉ cho vay hoặc hoàn trả theo doanh thu thì bỏ chọn Vốn cổ phần — website sẽ hiện đúng là Vốn vay / hoàn trả." />
          <HopChon nhan="Giai đoạn quỹ công bố (để trống nếu không giới hạn)" danhSach={options('STAGE')} chon={th.stages} onDoi={(v, on) => batIn('thesis', 'stages', v, on)} />
          <HopChon nhan="Địa bàn" danhSach={options('GEO')} chon={th.geos} onDoi={(v, on) => batIn('thesis', 'geos', v, on)} />
          <HopChon nhan="Ngành (để trống nếu không giới hạn)" danhSach={options('INDUSTRY')} chon={th.industries} onDoi={(v, on) => batIn('thesis', 'industries', v, on)} />
          <details className="o-rong"><summary>Thêm: mô hình kinh doanh, khách hàng, công cụ đầu tư</summary>
            <HopChon nhan="Mô hình kinh doanh" danhSach={options('BUSINESS_MODEL')} chon={th.businessModels} onDoi={(v, on) => batIn('thesis', 'businessModels', v, on)} />
            <HopChon nhan="Loại khách hàng" danhSach={options('CUSTOMER_TYPE')} chon={th.customerTypes} onDoi={(v, on) => batIn('thesis', 'customerTypes', v, on)} />
            <HopChon nhan="Công cụ đầu tư" danhSach={options('INSTRUMENT')} chon={th.instruments} onDoi={(v, on) => batIn('thesis', 'instruments', v, on)} />
          </details>
          <div><label>Yêu cầu đã có doanh thu</label><select value={triState(th.requiresRevenue)} onChange={(ev) => setIn('thesis', 'requiresRevenue', tuTriState(ev.target.value))}><option value="">Chưa rõ</option><option value="CO">Có</option><option value="KHONG">Không</option></select></div>
          <div><label>Yêu cầu đã thành lập công ty</label><select value={triState(th.requiresIncorporation)} onChange={(ev) => setIn('thesis', 'requiresIncorporation', tuTriState(ev.target.value))}><option value="">Chưa rõ</option><option value="CO">Có</option><option value="KHONG">Không</option></select></div>

          <div className="o-rong"><h2>Séc đầu tư</h2><p className="nho">Nhập theo đơn vị gốc quỹ công bố, không quy đổi. Để trống nếu quỹ không công bố.</p></div>
          <div><label>Tối thiểu</label><input type="number" min="0" value={tk.minSo} onChange={(ev) => setTk((o) => ({ ...o, minSo: ev.target.value }))} /></div>
          <div><label>Tối đa</label><input type="number" min="0" value={tk.maxSo} onChange={(ev) => setTk((o) => ({ ...o, maxSo: ev.target.value }))} /></div>
          <div><label>Đơn vị tiền</label><select value={tk.ccy} onChange={(ev) => setTk((o) => ({ ...o, ccy: ev.target.value }))}><option value="USD">USD</option><option value="VND">VND (đồng)</option></select></div>
          {tk.ccy === 'USD' && <div><label>Tỷ giá quy đổi (VND/USD)</label><input type="number" min="1" value={tk.fx} onChange={(ev) => setTk((o) => ({ ...o, fx: ev.target.value, fxDate: homNay }))} /></div>}
          <div><label>Con số này từ đâu</label><select value={t.sourceType || ''} onChange={(ev) => setTicket('sourceType', ev.target.value || undefined)}><option value="">— chưa chọn —</option>{LOAI_NGUON_TICKET.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
          <div><label>Bản chất con số</label><select value={t.basis || ''} onChange={(ev) => setTicket('basis', ev.target.value || undefined)}><option value="">— chưa chọn —</option>{CO_SO_TICKET.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
          <div><label>Độ tin cậy</label><select value={t.confidence || ''} onChange={(ev) => setTicket('confidence', ev.target.value || undefined)}><option value="">— chưa chọn —</option>{DO_TIN_CAY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
          {banGhi.thesis.ticket && <p className="o-rong nho">Website sẽ hiện: <strong>{ticketText(banGhi)}</strong></p>}

          <div className="o-rong"><h2>Kênh nộp hồ sơ</h2></div>
          <div className="o-rong"><label>Đường dẫn nộp hồ sơ hoặc liên hệ</label><input type="url" value={draft.process?.submitUrl || ''} placeholder="https://…" onChange={(ev) => setIn('process', 'submitUrl', ev.target.value)} /></div>
          <div><label>Đường dẫn trên là</label><select value={draft.process?.ctaType || ''} onChange={(ev) => setIn('process', 'ctaType', ev.target.value || undefined)}>
            <option value="">Chưa xác nhận (máy tự đoán)</option><option value="apply">Biểu mẫu nộp hồ sơ</option><option value="contact">Trang liên hệ chung</option></select></div>
          <div><label>Email nhận hồ sơ (nếu có)</label><input type="email" value={draft.process?.submitEmail || ''} onChange={(ev) => setIn('process', 'submitEmail', ev.target.value)} /></div>
          <div className="o-rong"><label>Ghi chú về cách nộp</label><input type="text" value={draft.process?.notes || ''} onChange={(ev) => setIn('process', 'notes', ev.target.value)} /></div>
          {cta && <p className="o-rong nho">Nút trên website sẽ hiện: <strong>{cta.label}</strong></p>}

          <div className="o-rong"><h2>Nội dung hiển thị</h2></div>
          <div className="o-rong"><label>Mô tả ngắn</label><textarea rows="3" value={draft.content?.summary || ''} onChange={(ev) => setIn('content', 'summary', ev.target.value)} /></div>
          <div className="o-rong"><label>Quỹ tìm kiếm (mỗi dòng một ý)</label><textarea rows="3" value={dong(draft.content?.lookingFor)} onChange={(ev) => setIn('content', 'lookingFor', ev.target.value.split('\n'))} /></div>
          <div className="o-rong"><label>Lưu ý cho founder (mỗi dòng một ý)</label><textarea rows="3" value={dong(draft.content?.tips)} onChange={(ev) => setIn('content', 'tips', ev.target.value.split('\n'))} /></div>

          <div className="o-rong"><h2>Điều kiện bắt buộc dùng để đối chiếu</h2>
            {th.hardRequirements?.length
              ? <><ul>{th.hardRequirements.map((r, i) => <li key={i}>{r.label}</li>)}</ul>
                <p className="nho">Đây là luật máy mà công cụ đối chiếu dùng để loại hồ sơ không đạt. Trang này giữ nguyên, không sửa được — cần thay đổi thì nhờ người kỹ thuật.</p></>
              : <p className="nho">Chưa có. Điều kiện chỉ để đọc thì ghi vào “Lưu ý cho founder”; điều kiện dùng để lọc hồ sơ cần người kỹ thuật thêm.</p>}
          </div>

          <div className="o-rong"><h2>Nguồn và kiểm chứng</h2>
            <p className="nho">Chỉ điền điều trang chính thức nói rõ. Mỗi dòng một nguồn, dạng <code>https://… | ghi chú</code>.</p></div>
          <div className="o-rong"><label>Nguồn</label><textarea rows="3" value={nguonText} onChange={(ev) => setNguonText(ev.target.value)} /></div>
          <div><label>Ngày kiểm chứng</label><input type="date" value={draft.provenance?.lastVerifiedAt || ''} onChange={(ev) => setIn('provenance', 'lastVerifiedAt', ev.target.value || null)} />
            <button type="button" className="text-button" onClick={() => setIn('provenance', 'lastVerifiedAt', homNay)}>Hôm nay</button></div>
        </div>

        {loi.length > 0 && <div className="notice"><strong>Cần bổ sung trước khi xuất bản:</strong><ul>{loi.map((x) => <li key={x}>{x}</li>)}</ul></div>}
        {draft.updatedAt && <p className="nho">Sửa lần cuối: {new Date(draft.updatedAt).toLocaleString('vi-VN')}{draft.updatedBy ? ` · ${draft.updatedBy}` : ''} · Trạng thái: {NHAN_TT[draft.publish?.status]}</p>}
        <div className="hang-nut">
          <button className="nut" disabled={busy} onClick={() => luu('PUBLISHED')}>Xuất bản</button>
          <button className="nut phu" disabled={busy} onClick={() => luu('DRAFT')}>Lưu nháp</button>
          {daLuu && draft.publish?.status !== 'HIDDEN' && <button className="nut phu" disabled={busy} onClick={() => luu('HIDDEN')}>Ẩn khỏi website</button>}
        </div>
        {daLuu && role === 'admin' && <XoaVinhVien id={draft.fundId} setMessage={setMessage} onXong={() => { moi(); refresh(); }} />}
      </section>
    </div>
  </div>;
}
